import React, { useState, useEffect } from 'react';
import { apiService } from '../../utils/api';
import { Bid, Rfq, BidType, BidStatus, RfqType, RfqStatus } from '../../types/api';
import { formatMoney } from '../../utils/money';
import styles from './MyInquiries.module.css';

type InquiryItem = (Bid & { inquiryType: 'bid' }) | (Rfq & { inquiryType: 'rfq' });

export const MyInquiries: React.FC = () => {
  const [inquiries, setInquiries] = useState<InquiryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInquiries = async () => {
      setLoading(true);
      try {
        // Fetch both bids and RFQs
        const [bidsResponse, rfqsResponse] = await Promise.all([
          apiService.getUserBids(),
          apiService.getUserRfqs()
        ]);

        // Combine and transform the data
        const combinedInquiries: InquiryItem[] = [
          ...bidsResponse.data.map(bid => ({ ...bid, inquiryType: 'bid' as const })),
          ...rfqsResponse.data.map(rfq => ({ ...rfq, inquiryType: 'rfq' as const }))
        ];

        // Sort by creation date (newest first)
        combinedInquiries.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setInquiries(combinedInquiries);
      } catch (err) {
        setError('Failed to load inquiries');
      } finally {
        setLoading(false);
      }
    };

    fetchInquiries();
  }, []);

  const getStatusBadge = (inquiry: InquiryItem) => {
    if (inquiry.inquiryType === 'rfq') {
      const status = inquiry.status;
      switch (status) {
        case RfqStatus.PENDING:
          return <span className={`${styles.badge} ${styles.badgePending}`}>Pending</span>;
        case RfqStatus.ACCEPTED:
          return <span className={`${styles.badge} ${styles.badgeAccepted}`}>Accepted</span>;
        case RfqStatus.REJECTED:
          return <span className={`${styles.badge} ${styles.badgeRejected}`}>Rejected</span>;
        case RfqStatus.CONTACTED:
          return <span className={`${styles.badge} ${styles.badgeContacted}`}>Contacted</span>;
        default:
          return <span className={`${styles.badge} ${styles.badgeDefault}`}>Unknown</span>;
      }
    } else if (inquiry.inquiryType === 'bid') {
      const rawStatus = (inquiry as any).status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : rawStatus;
      switch (status) {
        case BidStatus.PENDING:
        case 'PENDING':
          return <span className={`${styles.badge} ${styles.badgePending}`}>Pending</span>;
        case BidStatus.ACCEPTED:
        case 'ACCEPTED':
          return <span className={`${styles.badge} ${styles.badgeAccepted}`}>Accepted</span>;
        case BidStatus.REJECTED:
        case 'REJECTED':
          return <span className={`${styles.badge} ${styles.badgeRejected}`}>Rejected</span>;
        case BidStatus.CONTACTED:
        case 'CONTACTED':
          return <span className={`${styles.badge} ${styles.badgeContacted}`}>Contacted</span>;
        default:
          return <span className={`${styles.badge} ${styles.badgeDefault}`}>Bid</span>;
      }
    }
    return <span className={`${styles.badge} ${styles.badgeDefault}`}>Bid</span>;
  };

  const getTypeIcon = (inquiry: InquiryItem) => {
    // Removed arrow icons as requested
    return null;
  };

  const getTypeLabel = (inquiry: InquiryItem) => {
    const type = inquiry.type;
    return type === BidType.BUY || type === RfqType.BUY ? 'Buy' : 'Sell';
  };


  const handleDelete = (inquiry: InquiryItem) => {
    if (window.confirm(`Are you sure you want to delete this ${inquiry.inquiryType === 'bid' ? 'bid' : 'RFQ'}?`)) {
      // Remove from local state
      setInquiries(prev => prev.filter(item => {
        if (inquiry.inquiryType === 'bid' && item.inquiryType === 'bid') {
          return item.inquiry_id !== inquiry.inquiry_id;
        } else if (inquiry.inquiryType === 'rfq' && item.inquiryType === 'rfq') {
          return item.rfq_id !== inquiry.rfq_id;
        }
        return true;
      }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading inquiries...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <span>⚠️ {error}</span>
          <button 
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>My Inquiries</h2>
      
      {inquiries.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📋</div>
          <h3 className={styles.emptyTitle}>No inquiries yet</h3>
          <p className={styles.emptyDescription}>
            Your RFQs and bids will appear here once you create them.
          </p>
        </div>
      ) : (
        <div className={styles.inquiriesList}>
          {inquiries.map((inquiry) => (
            <div key={inquiry.inquiryType === 'bid' ? inquiry.inquiry_id : inquiry.rfq_id} className={styles.inquiryItem}>
              <div className={styles.inquiryHeader}>
                <div className={styles.inquiryType}>
                  <span className={styles.typeLabel}>{getTypeLabel(inquiry)}</span>
                </div>
                <div className={styles.headerActions}>
                  {getStatusBadge(inquiry)}
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDelete(inquiry)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className={styles.inquiryContent}>
                <div className={styles.projectInfo}>
                  {inquiry.logo && (
                    <img 
                      src={inquiry.logo} 
                      alt={inquiry.project_name}
                      className={styles.projectLogo}
                    />
                  )}
                  <div className={styles.projectDetails}>
                    <h4 className={styles.projectName}>{inquiry.project_name}</h4>
                    <p className={styles.inquiryDate}>{formatDate(inquiry.created_at)}</p>
                  </div>
                </div>
                
                <div className={styles.inquiryAmounts}>
                  <div className={styles.amountItem}>
                    <span className={styles.amountLabel}>Amount</span>
                    <span className={styles.amountValue}>{formatMoney(inquiry.tranche_size)}</span>
                  </div>
                  <div className={styles.amountItem}>
                    <span className={styles.amountLabel}>Valuation</span>
                    <span className={styles.amountValue}>{formatMoney(inquiry.valuation)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
